import { Button, Col, Divider, Row, Select } from "antd";
import { useContext, useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./style.module.scss";
import BannerCategoryContext from "../../../context/banner/context";
import AppContext from "../../../context/app/context";
import { IUpdateBanner } from "../../../models/banner/request";
import EndPoints from "../../../services/end-points";
import { getTranslations } from "../../../utils/helpers/translations";
import Spin from "../../general/antd/spin";
import { languages } from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import FormItem from "../../general/form-item";
import { getBannerType } from "../../../models/banner/enum";
import { getUserStatus } from "../../../models/user/enum";
import ImageUploader from "../../form-components/image-uploader";
import { MediumFor } from "../../../models/medium/enum";
import OldImage from "../../form-components/old-image";
import BannerCategoryContextProvider from "../../../context/banner/provider";
import { execute } from "../../../utils/helpers/execute";
import { ICharity } from "../../../models/charity/response";
import { IOrphan } from "../../../models/orphan/response";
import { ICampaign } from "../../../models/campaign/response";


const Form = () => {
  const { details, actions, loading } = useContext(BannerCategoryContext);

  const [charities, setCharities] = useState<ICharity[]>([]);
  const [charityLoading, setCharitiesLoading] = useState<boolean>(true);
  const [orphans, setOrphans] = useState<IOrphan[]>([]);
  const [orphanLoading, setOrphanLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<ICampaign[]>([]);
  const [campaignLoading, setCampaignsLoading] = useState<boolean>(true);


  const { direction } = useContext(AppContext);
  const Option = Select.Option;
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const banners = [1, 2, 3, 4, 5];

  const methods = useForm<any>({
    defaultValues: {
      imageId: details?.image?.id ?? undefined,
      rateableId: details?.rateableId ?? undefined,
      link: details?.link ?? undefined,
      for: 1,
    } as IUpdateBanner,
  });
  const { handleSubmit, reset, control, setValue } = methods;

  const rateableType = useWatch({
    control,
    name: "rateableType",
  });

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (
      scrollHeight - scrollTop === clientHeight &&
      hasMore &&
      !charityLoading
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  useEffect(() => {
    const getCharities = async () => {
      try {
        const { data } = await EndPoints.charity.getAllCharities({
          light: true,
          perPage: -1,
        });
        const newOptions = data?.data;
        setCharities((prevOptions) => [...prevOptions, ...newOptions]);
        setHasMore(data.lastPage == page ? false : true);
      } catch (error) {
      } finally {
        setCharitiesLoading(false);
      }
    };
    getCharities();
  }, [direction, page]);

  useEffect(() => {
    const getOrphans = async () => {
      try {
        const { data } = await EndPoints.orphan.getAllOrphans({
          light: true,
          perPage: -1,
        });
        setOrphans(data.data);
      } catch (error) {
      } finally {
        setOrphanLoading(false);
      }
    };
    getOrphans();
  }, [direction]);

  useEffect(() => {
    const getCampaigns = async () => {
      try {
        const { data } = await EndPoints.campaign.getAllCampaigns({
          light: true,
          perPage: -1,
        });
        setCampaigns(data.data);
      } catch (error) {
      } finally {
        setOrphanLoading(false);
      }
    };
    getCampaigns();
  }, [direction]);

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const onSubmit = async (data: any) => {
    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        details
          ? await EndPoints.banner.updateBanner(details?.id, data)
          : await EndPoints.banner.createBanner(data);
        navigate(-1);
      },
      fallback: (error) => { },
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  useEffect(() => {
    reset({
      imageId: details?.image?.id,
      link: details?.link,
      rateableId: details?.rateableId,
      rateableType: details?.rateableType,
      status: details?.status,
      description: details?.description || {},
      text: details?.text || {}
    } as IUpdateBanner);
  }, [details]);

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {details ? t("update_banner") : t("add_banner")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <div className={styles.children}>
              <Row gutter={[8, 8]}>
                {languages?.map((lang, index) => {
                  return (
                    <Col span={12}>
                      <FieldBuilder
                        key={index}
                        name={`text[${lang?.code}]`}
                        label={`${t("text")} (${lang?.name})`}
                        input={{ type: "text" }}
                        rules={{ required: true }}
                      />
                    </Col>
                  );
                })}

                <Col span={12}>
                  <FormItem label={t("rateable_type")} required>
                    <Controller
                      name="rateableType"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { ...field } }) => {
                        return (
                          <Select
                            {...field}
                            onSelect={() => {
                              setValue("rateableId", null);
                            }}
                            style={{ width: "100%" }}
                            placeholder={t("rateable_type")}
                          >
                            {banners?.map((inc, index) => {
                              return (
                                <Option key={index} value={inc}>
                                  {t(getBannerType(inc))}
                                </Option>
                              );
                            })}
                          </Select>
                        );
                      }}
                    />
                  </FormItem>
                </Col>
                {!rateableType && (
                  <Col span={12}>
                    <FieldBuilder
                      name=""
                      label={t("choice_rateable_type")}
                      input={{ type: "text" }}
                      disabled
                      rules={{ required: false }}
                    />
                  </Col>
                )}
                {rateableType === 3 && (
                  <Col span={12}>
                    <FieldBuilder
                      name="rateableId"
                      label={`${t("charity")}`}
                      width="large"
                      input={{
                        type: "select-pagination",
                        handleScroll: handleScroll,
                        allowSearch: true,
                        loading: charityLoading,
                        options: charities.map((product, index) => {
                          return {
                            label: product.name,
                            value: product.id,
                          };
                        }),
                      }}
                    />
                  </Col>
                )}

                {rateableType === 4 && (
                  <Col span={12}>
                    <FieldBuilder
                      name="rateableId"
                      label={t("campaign")}
                      width="large"
                      input={{
                        type: "select",
                        allowSearch: true,
                        loading: campaignLoading,
                        options: campaigns?.map((tr, index) => {
                          return {
                            label: tr.name,
                            value: tr.id,
                          };
                        }),
                      }}
                    />
                  </Col>
                )}
                {rateableType === 5 && (
                  <Col span={12}>
                    <FieldBuilder
                      name="rateableId"
                      label={t("orphan")}
                      width="large"
                      input={{
                        type: "select",
                        allowSearch: true,
                        loading: orphanLoading,
                        options: orphans?.map((tr, index) => {
                          return {
                            label: tr.firstName,
                            value: tr.id,
                          };
                        }),
                      }}
                    />
                  </Col>
                )}


                {rateableType === 2 && (
                  <Col span={12}>
                    <FieldBuilder
                      name="link"
                      label={t("link")}
                      input={{ type: "text" }}
                      rules={{ required: false }}
                    />
                  </Col>
                )}

                {rateableType === 1 && (
                  <Col span={12}>
                    <FieldBuilder
                      name="not_related"
                      disabled
                      label={t("not_related")}
                      input={{ type: "text" }}
                      rules={{ required: false }}
                    />
                  </Col>
                )}

                <Col span={12}>
                  <FormItem label={t("status")}>
                    <Controller
                      name="status"
                      control={control}
                      rules={{ required: false }}
                      render={({ field: { ...field } }) => {
                        return (
                          <Select
                            loading={false}
                            // disabled={countryId ? false : true}
                            {...field}
                            style={{ width: "100%" }}
                            allowClear
                            placeholder={t("status")}
                          >
                            {[1, 2]?.map((tr, index) => {
                              return (
                                <Option key={index} value={tr}>
                                  {t(getUserStatus(tr))}
                                </Option>
                              );
                            })}
                          </Select>
                        );
                      }}
                    />
                  </FormItem>
                </Col>
                <Col span={12}></Col>
                {languages?.map((lang, index) => {
                  return (
                    <Col span={12}>
                      <FieldBuilder
                        key={index}
                        name={`description[${lang?.code}]`}
                        label={`${t("description")} (${lang?.name})`}
                        input={{ type: "text-area" }}
                        rules={{ required: false }}
                      />
                    </Col>
                  );
                })}

                <Col span={24}>
                  <FormItem label={t("image")} required>
                    <Controller
                      name="imageId"
                      rules={{
                        required: {
                          value: true,
                          message: `${t("field_is_required_message")}`,
                        },
                      }}
                      render={({ field: { ref, ...field } }) => {
                        return (
                          <ImageUploader
                            {...field}
                            type="DRAGGER"
                            onChange={(response) => {
                              // setfileId(response);
                              field.onChange(response?.id);
                            }}
                            imageFor={MediumFor.UserProfile}
                          />
                        );
                      }}
                    />
                  </FormItem>
                </Col>
                {details?.image && <OldImage src={details?.image?.url} />}
              </Row>
            </div>
          )}
        </div>
        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button
              onClick={() => {
                navigate(-1);
              }}
            >
              {t("cancel")}
            </Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const BannerCampaignScreenForm = () => {
  return (
    <BannerCategoryContextProvider>
      <Form />
    </BannerCategoryContextProvider>
  );
};
export default BannerCampaignScreenForm;
